import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CalendarClock, FileText, MessageSquare, Users } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/shared/components/page-header';
import { EmptyState } from '@/shared/components/empty-state';
import { Wizard, type WizardStep } from '@/shared/components/wizard';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { cn } from '@/shared/lib/cn';
import { usePassbook } from '@/shared/lib/passbook';
import { DEFAULT_SMS_QUOTA } from '@/features/channel-quota/model/ledger';
import { useChannelQuotas } from '@/features/channel-quota/model/use-channel-quotas';
import {
  applyMerge,
  AudienceBreakdown,
  CHANNEL_COPY,
  ChannelErrorState,
  ChannelLoading,
  ChoiceCard,
  ConsumptionPanel,
  formatCount,
  marketingErrorToast,
  reviewConsumption,
  ReviewSummaryCard,
  SendSchedule,
  snapshotFromSegment,
  toMarketingError,
  type AccountCapacity,
  type AudienceSegment,
  type SendMode,
} from '@/features/marketing-shared';
import { BackToSmsCampaigns } from './campaign-actions';
import { useSmsMarketing } from '../model/context';
import { useSmsMarketingPaths } from '../model/paths';
import { useSmsAudiences } from '../model/use-sms-audiences';
import { measureSmsText, smsEncodingLabel, type SmsTextMeasure } from '../model/text';
import type { SmsAccountNumber, SmsCampaign } from '../model/types';
import { SmsComposer } from './sms-composer';
import { SmsPhonePreview, smsPreviewLayoutClass } from './sms-preview';

const DEFAULT_TEXT = '{{primeiro_nome}}, você tem {{pontos}} pontos na {{loja}}.';

function localToIso(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function isoToLocal(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Amanhã às 10h: um horário futuro razoável para começar a editar. */
function defaultScheduleLocal(now = new Date()): string {
  const next = new Date(now);
  next.setDate(next.getDate() + 1);
  next.setHours(10, 0, 0, 0);
  return isoToLocal(next.toISOString());
}

function wizardTitle(existing: boolean) {
  return existing ? 'Editar campanha de SMS' : 'Nova campanha de SMS';
}

export function SmsCampaignWizardPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const paths = useSmsMarketingPaths();
  const { campaigns, campaignsState, numbers, numbersState } = useSmsMarketing();
  const audiencesQuery = useSmsAudiences();
  const existing = campaignId ? campaigns.find((item) => item.id === campaignId) ?? null : null;

  const states = [campaignsState, numbersState, audiencesQuery.state];
  const failed = states.find((state) => state.phase === 'error');
  const loading = states.some((state) => state.phase === 'loading');

  if (failed) {
    return (
      <>
        <PageHeader title={wizardTitle(Boolean(campaignId))} leading={<BackToSmsCampaigns />} />
        <ChannelErrorState
          title="Não foi possível abrir a criação"
          error={failed.error}
          onRetry={() => states.forEach((state) => state.refetch())}
        />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <PageHeader
          title={wizardTitle(Boolean(campaignId))}
          description="Público, texto e revisão."
          leading={<BackToSmsCampaigns />}
        />
        <ChannelLoading label="Carregando a criação do SMS" />
      </>
    );
  }

  if (campaignId && !existing) {
    return (
      <>
        <PageHeader title="Editar campanha de SMS" leading={<BackToSmsCampaigns />} />
        <Card>
          <EmptyState
            icon={MessageSquare}
            title="Campanha não encontrada"
            description="Esse rascunho não existe nesta loja ou já foi excluído."
            action={
              <Button type="button" onClick={() => navigate(paths.list)}>
                Voltar às campanhas
              </Button>
            }
          />
        </Card>
      </>
    );
  }

  if (existing && existing.status !== 'draft') {
    return (
      <>
        <PageHeader title="Editar campanha de SMS" leading={<BackToSmsCampaigns />} />
        <Card>
          <EmptyState
            icon={MessageSquare}
            title="Só rascunho abre o editor"
            description="Os outros estados mostram o detalhe. Duplicar gera um rascunho novo."
            action={
              <Button type="button" onClick={() => navigate(paths.detail(existing.id))}>
                Ver campanha
              </Button>
            }
          />
        </Card>
      </>
    );
  }

  return <WizardForm key={campaignId ?? 'nova'} existing={existing} numbers={numbers} audiences={audiencesQuery.audiences} />;
}

function WizardForm({
  existing,
  numbers,
  audiences,
}: {
  existing: SmsCampaign | null;
  numbers: SmsAccountNumber[];
  audiences: AudienceSegment[];
}) {
  const navigate = useNavigate();
  const { idPassbook, passbook } = usePassbook();
  const { quotas } = useChannelQuotas(idPassbook);
  const paths = useSmsMarketingPaths();
  const { approveCampaign } = useSmsMarketing();
  const storeName = passbook.organizationName || 'Sua loja';
  const activeSenders = numbers.filter((item) => !item.paused);
  const defaultAudience =
    (existing && audiences.find((item) => item.id === existing.audience.audienceId)) ??
    audiences.find((item) => item.eligible > 0) ??
    audiences[0] ??
    null;

  const [step, setStep] = useState(0);
  const [name, setName] = useState(existing?.name ?? '');
  const [from, setFrom] = useState(existing?.from || activeSenders[0]?.telephoneNumber || '');
  const [audienceId, setAudienceId] = useState(defaultAudience?.id ?? '');
  const [text, setText] = useState(existing?.text || DEFAULT_TEXT);
  const [sendMode, setSendMode] = useState<SendMode>(existing?.sendMode === 'now' ? 'now' : 'later');
  const [scheduledAt, setScheduledAt] = useState(isoToLocal(existing?.scheduledAt ?? null) || defaultScheduleLocal());
  const [partialSend, setPartialSend] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverBlocks, setServerBlocks] = useState<string[]>([]);

  const audience = audiences.find((item) => item.id === audienceId) ?? null;
  const preview = applyMerge(text, storeName);
  const measure = measureSmsText(preview);
  const raw = measureSmsText(text);
  const capacity = quotas?.sms ?? DEFAULT_SMS_QUOTA;
  const review = reviewConsumption(audience?.eligible ?? 0, capacity, {
    unitsPerPerson: measure.parts,
    unit: CHANNEL_COPY.sms.unit,
    missingUnitsMessage: CHANNEL_COPY.sms.missingUnits,
  });
  const textBlocked = !text.trim() || raw.exceedsApiMax || raw.strayBraces || !from;

  const approve = async () => {
    const sendingPartial = partialSend && review.canSendPartial;
    if (!audience || textBlocked || (!review.canApprove && !sendingPartial)) return;
    const when = sendMode === 'later' ? localToIso(scheduledAt) : null;
    if (sendMode === 'later' && !when) {
      toast.error('Informe data e hora para agendar.');
      return;
    }
    setSubmitting(true);
    setServerBlocks([]);
    try {
      const result = await approveCampaign(
        {
          id: existing?.status === 'draft' ? existing.id : undefined,
          name,
          from,
          text,
          audience: snapshotFromSegment(audience),
          scheduledAt: when,
          sendMode,
        },
        { allowPartial: sendingPartial },
      );
      const people = result.campaign.audience.eligible;
      toast.success(result.leftOut > 0 ? 'Campanha de SMS aprovada com envio parcial.' : 'Campanha de SMS aprovada e agendada.', {
        description:
          result.leftOut > 0
            ? `${formatCount(people)} contatos entram. ${formatCount(result.leftOut)} ficam de fora. Os slots foram reservados no saldo da loja.`
            : `${formatCount(people)} contatos. Os segmentos foram reservados no saldo da loja; o envio sai pela agenda do FixeCRM.`,
      });
      navigate(paths.detail(result.campaign.id));
    } catch (error) {
      const mapped = toMarketingError(error, 'Não foi possível aprovar a campanha.');
      setServerBlocks(mapped.blocks.length ? mapped.blocks : [mapped.message]);
      const { message, description } = marketingErrorToast(error, 'Não foi possível aprovar a campanha.');
      toast.error(message, { description });
      setStep(2);
    } finally {
      setSubmitting(false);
    }
  };

  const steps: WizardStep[] = [
    {
      id: 'publico',
      title: 'Público',
      icon: Users,
      content: (
        <AudienceStep
          name={name}
          onName={setName}
          from={from}
          onFrom={setFrom}
          numbers={numbers}
          audiences={audiences}
          audienceId={audienceId}
          onAudience={setAudienceId}
          audience={audience}
          segmentationsPath={`/loja/${idPassbook}/segmentacoes`}
        />
      ),
    },
    {
      id: 'conteudo',
      title: 'Conteúdo',
      icon: FileText,
      content: (
        <ContentStep text={text} onText={setText} storeName={storeName} from={from} />
      ),
    },
    {
      id: 'revisao',
      title: 'Revisão',
      icon: CalendarClock,
      content: (
        <ReviewStep
          name={name}
          from={from}
          audience={audience}
          preview={preview}
          measure={measure}
          sendMode={sendMode}
          onSendMode={setSendMode}
          scheduledAt={scheduledAt}
          onScheduledAt={setScheduledAt}
          eligible={audience?.eligible ?? 0}
          capacity={capacity}
          passbookId={idPassbook}
          campaignName={name}
          partialAccepted={partialSend}
          onPartialAccepted={setPartialSend}
          serverBlocks={serverBlocks}
        />
      ),
    },
  ];

  const nextDisabled = step === 0 ? !name.trim() || !from || !audience || audience.eligible === 0 : textBlocked;
  const sendingPartial = partialSend && review.canSendPartial;
  const finishDisabled = (!review.canApprove && !sendingPartial) || !name.trim() || textBlocked || (sendMode === 'later' && !scheduledAt);

  return (
    <div className="min-w-0">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <PageHeader
          className="mb-0 min-w-0 max-w-full shrink-0"
          title={wizardTitle(Boolean(existing))}
          description="Público, texto e revisão. A aprovação reserva o saldo e agenda o envio."
          leading={<BackToSmsCampaigns />}
        />
        {audience ? (
          <AudienceBreakdown
            className="max-w-full justify-end"
            audience={snapshotFromSegment(audience)}
            tone="kpi"
          />
        ) : null}
      </div>
      <Wizard
        steps={steps}
        stepIndex={step}
        onStepIndexChange={setStep}
        onCancel={() => navigate(paths.list)}
        onFinish={approve}
        finishLabel="Aprovar agendamento"
        finishLoadingLabel="Aprovando…"
        isSubmitting={submitting}
        finishDisabled={finishDisabled}
        nextDisabled={nextDisabled}
        mobileOptimized
        clickableStepper
      />
    </div>
  );
}

function AudienceStep({
  name,
  onName,
  from,
  onFrom,
  numbers,
  audiences,
  audienceId,
  onAudience,
  audience,
  segmentationsPath,
}: {
  name: string;
  onName: (value: string) => void;
  from: string;
  onFrom: (value: string) => void;
  numbers: SmsAccountNumber[];
  audiences: AudienceSegment[];
  audienceId: string;
  onAudience: (value: string) => void;
  audience: AudienceSegment | null;
  segmentationsPath: string;
}) {
  const hasActive = numbers.some((item) => !item.paused);
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="nome-sms">Nome da campanha</Label>
          <Input
            id="nome-sms"
            className="mt-1.5"
            value={name}
            onChange={(event) => onName(event.target.value)}
            placeholder="Ex.: Lembrete de pontos"
            autoComplete="off"
          />
          {!name.trim() ? (
            <p className="mt-1 text-sm text-destructive" role="status">
              Dê um nome para continuar.
            </p>
          ) : null}
        </div>
        <div>
          <Label htmlFor="remetente-sms">Remetente</Label>
          <Select value={from} onValueChange={onFrom} disabled={numbers.length === 0}>
            <SelectTrigger id="remetente-sms" className="mt-1.5 w-full">
              <SelectValue placeholder={numbers.length === 0 ? 'Nenhum número liberado' : 'Escolha o número'} />
            </SelectTrigger>
            <SelectContent>
              {numbers.map((item) => (
                <SelectItem key={item.id} value={item.telephoneNumber} disabled={item.paused}>
                  {item.telephoneNumber}
                  {item.paused ? ' · pausado' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!hasActive ? (
            <p className="mt-1 text-sm text-destructive" role="status">
              Nenhum remetente ativo para esta loja. Peça ao time da Fixe para liberar um número.
            </p>
          ) : null}
        </div>
      </div>
      <fieldset className="space-y-3">
        <legend className="text-sm font-medium">Destinatários</legend>
        {audiences.length === 0 ? (
          <Card className="p-4 text-sm">
            <p className="font-medium">Nenhum público disponível</p>
            <p className="mt-1 text-muted-foreground">
              Crie uma segmentação e clique em “Atualizar público-alvo” para ela aparecer aqui.
            </p>
            <Button type="button" variant="secondary" size="sm" className="mt-3" asChild>
              <Link to={segmentationsPath}>Ir para segmentações</Link>
            </Button>
          </Card>
        ) : (
          <RadioGroup value={audienceId} onValueChange={onAudience} aria-label="Público do SMS" className="gap-3">
            {audiences.map((item) => (
              <ChoiceCard
                key={item.id}
                selected={item.id === audienceId}
                onSelect={() => onAudience(item.id)}
                control={<RadioGroupItem value={item.id} id={`sms-publico-${item.id}`} aria-labelledby={`sms-publico-nome-${item.id}`} className="mt-0.5" />}
              >
                <p id={`sms-publico-nome-${item.id}`} className="break-words text-base font-medium">
                  {item.name}
                </p>
                {item.description ? <p className="mt-1 text-sm text-muted-foreground">{item.description}</p> : null}
                <p className="mt-2 text-sm">
                  <span className={cn('font-medium tabular-nums', item.eligible === 0 && 'text-destructive')}>
                    {formatCount(item.eligible)} elegíveis
                  </span>
                  <span className="text-muted-foreground">
                    {' '}
                    · {formatCount(item.excludedNoConsent)} sem consentimento · {formatCount(item.excludedUnsubscribed)} descadastrados ·{' '}
                    {formatCount(item.excludedInvalid)} inválidos
                  </span>
                </p>
              </ChoiceCard>
            ))}
          </RadioGroup>
        )}
      </fieldset>
      {audience && audience.eligible === 0 ? (
        <p className="rounded-xl border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive" role="status">
          Ninguém pode receber este SMS. Sem consentimento, descadastrados e inválidos cobrem a lista inteira.
        </p>
      ) : null}
    </div>
  );
}

function ContentStep({
  text,
  onText,
  storeName,
  from,
}: {
  text: string;
  onText: (value: string) => void;
  storeName: string;
  from: string;
}) {
  const preview = applyMerge(text, storeName);
  return (
    <div className={smsPreviewLayoutClass}>
      <SmsComposer id="texto-sms" value={text} onChange={onText} preview={preview} storeName={storeName} />
      <SmsPhonePreview message={preview} sender={from} followTyping />
    </div>
  );
}

function partsPerPerson(parts: number): string {
  if (parts <= 0) return 'Nenhum SMS';
  if (parts === 1) return '1 SMS por pessoa';
  return `${formatCount(parts)} SMS por pessoa`;
}

function ReviewStep({
  name,
  from,
  audience,
  preview,
  measure,
  sendMode,
  onSendMode,
  scheduledAt,
  onScheduledAt,
  eligible,
  capacity,
  passbookId,
  campaignName,
  partialAccepted,
  onPartialAccepted,
  serverBlocks,
}: {
  name: string;
  from: string;
  audience: AudienceSegment | null;
  preview: string;
  measure: SmsTextMeasure;
  sendMode: SendMode;
  onSendMode: (value: SendMode) => void;
  scheduledAt: string;
  onScheduledAt: (value: string) => void;
  eligible: number;
  capacity: AccountCapacity;
  passbookId: string;
  campaignName: string;
  partialAccepted: boolean;
  onPartialAccepted: (value: boolean) => void;
  serverBlocks: string[];
}) {
  return (
    <div className="space-y-4">
      {serverBlocks.length > 0 ? (
        <section className="rounded-2xl border border-destructive/40 bg-destructive/5 p-4 sm:p-5" role="alert">
          <h2 className="text-base font-semibold">O servidor recusou a aprovação</h2>
          <ul className="mt-2 space-y-1">
            {serverBlocks.map((block) => (
              <li key={block} className="text-sm font-medium text-destructive">
                {block}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-sm text-muted-foreground">Corrija o que está listado e aprove de novo. Nada foi reservado.</p>
        </section>
      ) : null}
      <SendSchedule
        idPrefix="sms-envio"
        sendMode={sendMode}
        onSendMode={onSendMode}
        scheduledAt={scheduledAt}
        onScheduledAt={onScheduledAt}
      />
      <div className={smsPreviewLayoutClass}>
        <ReviewSummaryCard
          rows={[
            { label: 'Campanha', value: name },
            { label: 'Remetente', value: from },
            { label: 'Destinatários', value: audience ? `${audience.name} · ${formatCount(audience.eligible)}` : '—' },
            {
              label: 'Codificação',
              value: measure.concatenated
                ? `${smsEncodingLabel(measure.encoding)} · ${formatCount(measure.singlePartLimit)} na primeira, ${formatCount(measure.multipartLimit)} nas seguintes`
                : `${smsEncodingLabel(measure.encoding)} · ${formatCount(measure.singlePartLimit)} por SMS`,
            },
            { label: 'Partes', value: partsPerPerson(measure.parts) },
          ]}
        >
          <p className="mt-4 whitespace-pre-wrap break-words text-sm leading-relaxed">{preview}</p>
        </ReviewSummaryCard>
        <SmsPhonePreview message={preview} sender={from} />
      </div>
      <ConsumptionPanel
        eligible={eligible}
        capacity={capacity}
        channel="sms"
        segments={measure.parts}
        passbookId={passbookId}
        campaignName={campaignName}
        partialAccepted={partialAccepted}
        onPartialAccepted={onPartialAccepted}
      />
    </div>
  );
}
